import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Paper,
  Badge,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  FileCopy as CopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Group as TeamIcon,
  Assignment as TaskIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Brush as DesignIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Api as ApiIcon,
  Storage as DatabaseIcon,
  Campaign as MarketingIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../../contexts/LoadingContext';
import { useNotify } from '../../ui/NotificationSystem';

interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedDuration: string;
  estimatedBudget: { min: number; max: number };
  requiredSkills: string[];
  teamSize: { min: number; max: number };
  isFavorite: boolean;
  isPopular: boolean;
  usageCount: number;
  lastUsed?: string;
  features: string[];
  deliverables: string[];
  milestones: TemplateMilestone[];
  requirements: string[];
  icon: React.ReactNode;
  color: string;
}

interface TemplateMilestone {
  title: string;
  description: string;
  estimatedDays: number;
  dependencies?: string[];
}

const TEMPLATE_CATEGORIES = [
  'All Templates',
  'Web Development',
  'Mobile Development',
  'E-commerce',
  'Design',
  'Marketing',
  'API Development',
  'Database',
  'Consultation',
  'Support'
];

const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: '1',
    name: 'React Web Application',
    description: 'Complete React-based web application with modern UI/UX, state management, and responsive design.',
    category: 'Web Development',
    difficulty: 'Intermediate',
    estimatedDuration: '8-12 weeks',
    estimatedBudget: { min: 15000, max: 35000 },
    requiredSkills: ['React', 'TypeScript', 'Node.js', 'CSS/Sass', 'REST APIs'],
    teamSize: { min: 3, max: 5 },
    isFavorite: true,
    isPopular: true,
    usageCount: 45,
    lastUsed: '2024-10-15',
    icon: <CodeIcon />,
    color: '#61DAFB',
    features: [
      'Responsive Design',
      'User Authentication',
      'State Management (Redux/Context)',
      'API Integration',
      'Testing Suite',
      'CI/CD Pipeline'
    ],
    deliverables: [
      'Functional Web Application',
      'Source Code Repository',
      'Documentation',
      'Deployment Scripts',
      'User Manual',
      'Testing Reports'
    ],
    milestones: [
      { title: 'Project Setup & Planning', description: 'Initialize project structure and development environment', estimatedDays: 3 },
      { title: 'UI/UX Design', description: 'Create wireframes, mockups, and design system', estimatedDays: 10 },
      { title: 'Frontend Development', description: 'Implement React components and user interface', estimatedDays: 25 },
      { title: 'Backend Integration', description: 'Connect frontend with APIs and services', estimatedDays: 15 },
      { title: 'Testing & Quality Assurance', description: 'Comprehensive testing and bug fixes', estimatedDays: 10 },
      { title: 'Deployment & Launch', description: 'Deploy to production and go-live', estimatedDays: 5 }
    ],
    requirements: [
      'Define project scope and requirements',
      'User authentication system',
      'Responsive web design',
      'Cross-browser compatibility',
      'Performance optimization',
      'Security best practices'
    ]
  },
  {
    id: '2',
    name: 'E-commerce Platform',
    description: 'Full-featured e-commerce solution with product management, payment processing, and admin dashboard.',
    category: 'E-commerce',
    difficulty: 'Advanced',
    estimatedDuration: '12-16 weeks',
    estimatedBudget: { min: 25000, max: 60000 },
    requiredSkills: ['React', 'Node.js', 'PostgreSQL', 'Stripe', 'AWS', 'Docker'],
    teamSize: { min: 4, max: 7 },
    isFavorite: false,
    isPopular: true,
    usageCount: 32,
    lastUsed: '2024-10-12',
    icon: <EcommerceIcon />,
    color: '#FF6B35',
    features: [
      'Product Catalog Management',
      'Shopping Cart & Checkout',
      'Payment Processing',
      'Order Management',
      'Inventory Tracking',
      'Admin Dashboard',
      'Customer Accounts',
      'Email Notifications'
    ],
    deliverables: [
      'E-commerce Web Application',
      'Admin Dashboard',
      'Mobile-Responsive Design',
      'Payment Gateway Integration',
      'Database Schema',
      'API Documentation',
      'User Training Materials'
    ],
    milestones: [
      { title: 'Architecture & Setup', description: 'Design system architecture and setup development environment', estimatedDays: 5 },
      { title: 'Database Design', description: 'Design and implement database schema', estimatedDays: 7 },
      { title: 'Product Management', description: 'Build product catalog and management features', estimatedDays: 20 },
      { title: 'Shopping Cart & Checkout', description: 'Implement cart functionality and checkout process', estimatedDays: 18 },
      { title: 'Payment Integration', description: 'Integrate payment gateways and order processing', estimatedDays: 15 },
      { title: 'Admin Dashboard', description: 'Build admin interface for store management', estimatedDays: 20 },
      { title: 'Testing & Launch', description: 'Comprehensive testing and production deployment', estimatedDays: 10 }
    ],
    requirements: [
      'Product catalog with categories',
      'Shopping cart functionality',
      'Secure payment processing',
      'Order management system',
      'User account management',
      'Admin dashboard',
      'Inventory management',
      'Email notifications'
    ]
  },
  {
    id: '3',
    name: 'Mobile App (React Native)',
    description: 'Cross-platform mobile application for iOS and Android with native performance and modern UX.',
    category: 'Mobile Development',
    difficulty: 'Intermediate',
    estimatedDuration: '10-14 weeks',
    estimatedBudget: { min: 20000, max: 45000 },
    requiredSkills: ['React Native', 'JavaScript', 'Mobile UI/UX', 'Firebase', 'App Store Deployment'],
    teamSize: { min: 3, max: 5 },
    isFavorite: true,
    isPopular: false,
    usageCount: 28,
    lastUsed: '2024-10-08',
    icon: <MobileIcon />,
    color: '#007AFF',
    features: [
      'Cross-Platform Compatibility',
      'Native Performance',
      'Push Notifications',
      'Offline Functionality',
      'Camera Integration',
      'Location Services',
      'Social Login',
      'App Store Ready'
    ],
    deliverables: [
      'iOS Application',
      'Android Application',
      'App Store Listings',
      'Backend Services',
      'User Documentation',
      'Deployment Guide'
    ],
    milestones: [
      { title: 'App Architecture', description: 'Define app structure and navigation flow', estimatedDays: 5 },
      { title: 'UI/UX Design', description: 'Create mobile-first design and user experience', estimatedDays: 12 },
      { title: 'Core Features Development', description: 'Implement main app functionality', estimatedDays: 30 },
      { title: 'Platform Integration', description: 'Integrate with device features and services', estimatedDays: 15 },
      { title: 'Testing & Optimization', description: 'Test on multiple devices and optimize performance', estimatedDays: 12 },
      { title: 'App Store Submission', description: 'Prepare and submit to app stores', estimatedDays: 8 }
    ],
    requirements: [
      'Cross-platform mobile app',
      'Native device integration',
      'Push notification system',
      'Offline functionality',
      'User authentication',
      'App store compliance',
      'Performance optimization'
    ]
  },
  {
    id: '4',
    name: 'API Development & Documentation',
    description: 'RESTful API development with comprehensive documentation, testing, and deployment automation.',
    category: 'API Development',
    difficulty: 'Intermediate',
    estimatedDuration: '6-8 weeks',
    estimatedBudget: { min: 12000, max: 25000 },
    requiredSkills: ['Node.js', 'Express', 'MongoDB/PostgreSQL', 'Swagger', 'Docker', 'AWS'],
    teamSize: { min: 2, max: 4 },
    isFavorite: false,
    isPopular: true,
    usageCount: 38,
    lastUsed: '2024-10-14',
    icon: <ApiIcon />,
    color: '#4CAF50',
    features: [
      'RESTful API Design',
      'Authentication & Authorization',
      'Rate Limiting',
      'API Documentation',
      'Testing Suite',
      'Monitoring & Logging',
      'Auto-Scaling',
      'Security Best Practices'
    ],
    deliverables: [
      'API Server Application',
      'Interactive API Documentation',
      'Testing Suite',
      'Deployment Scripts',
      'Monitoring Dashboard',
      'Developer Guide'
    ],
    milestones: [
      { title: 'API Design & Planning', description: 'Design API endpoints and data models', estimatedDays: 5 },
      { title: 'Core API Development', description: 'Implement API endpoints and business logic', estimatedDays: 18 },
      { title: 'Authentication & Security', description: 'Implement security measures and access control', estimatedDays: 8 },
      { title: 'Documentation & Testing', description: 'Create documentation and comprehensive tests', estimatedDays: 10 },
      { title: 'Deployment & Monitoring', description: 'Deploy to production with monitoring setup', estimatedDays: 7 }
    ],
    requirements: [
      'RESTful API architecture',
      'Comprehensive documentation',
      'Authentication system',
      'Rate limiting and security',
      'Automated testing',
      'Production deployment',
      'Monitoring and logging'
    ]
  },
  {
    id: '5',
    name: 'UI/UX Design System',
    description: 'Complete design system with component library, style guide, and design tokens for consistent branding.',
    category: 'Design',
    difficulty: 'Intermediate',
    estimatedDuration: '4-6 weeks',
    estimatedBudget: { min: 8000, max: 18000 },
    requiredSkills: ['Figma', 'Adobe XD', 'Design Systems', 'Typography', 'Color Theory', 'Accessibility'],
    teamSize: { min: 2, max: 3 },
    isFavorite: true,
    isPopular: false,
    usageCount: 22,
    lastUsed: '2024-10-10',
    icon: <DesignIcon />,
    color: '#FF5722',
    features: [
      'Component Library',
      'Design Tokens',
      'Style Guide',
      'Accessibility Guidelines',
      'Responsive Patterns',
      'Icon Library',
      'Typography System',
      'Color Palette'
    ],
    deliverables: [
      'Figma Design System',
      'Component Library',
      'Style Guide Documentation',
      'Icon Set',
      'Brand Guidelines',
      'Design Tokens (JSON/CSS)'
    ],
    milestones: [
      { title: 'Brand Analysis & Research', description: 'Analyze current brand and user needs', estimatedDays: 3 },
      { title: 'Design Foundation', description: 'Establish colors, typography, and spacing', estimatedDays: 7 },
      { title: 'Component Design', description: 'Create reusable UI components', estimatedDays: 12 },
      { title: 'Documentation', description: 'Create comprehensive style guide', estimatedDays: 5 },
      { title: 'Implementation Guide', description: 'Provide implementation guidelines for developers', estimatedDays: 3 }
    ],
    requirements: [
      'Comprehensive component library',
      'Consistent design language',
      'Accessibility compliance',
      'Responsive design patterns',
      'Implementation guidelines',
      'Brand consistency',
      'Scalable design system'
    ]
  }
];

const ProjectTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { success: showSuccess, error: showError } = useNotify();
  
  const [selectedCategory, setSelectedCategory] = useState('All Templates');
  const [previewDialog, setPreviewDialog] = useState<ProjectTemplate | null>(null);
  const [createDialog, setCreateDialog] = useState<ProjectTemplate | null>(null);
  const [templates, setTemplates] = useState<ProjectTemplate[]>(PROJECT_TEMPLATES);

  const filteredTemplates = selectedCategory === 'All Templates'
    ? templates
    : templates.filter(template => template.category === selectedCategory);

  const handleToggleFavorite = (templateId: string) => {
    setTemplates(prev => prev.map(template =>
      template.id === templateId
        ? { ...template, isFavorite: !template.isFavorite }
        : template
    ));
  };

  const handleUseTemplate = async (template: ProjectTemplate) => {
    setLoading('useTemplate', true);
    try {
      // Simulate using template
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to create project page with template data
      navigate('/dashboard/admin/projects/create', { 
        state: { template } 
      });
      
      showSuccess(`Using template: ${template.name}`);
    } catch (error) {
      showError('Failed to use template');
    } finally {
      setLoading('useTemplate', false);
    }
  };

  const handleCreateFromTemplate = (template: ProjectTemplate) => {
    setCreateDialog(template);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'error';
      default: return 'default';
    }
  };

  const renderTemplateCard = (template: ProjectTemplate) => (
    <Grid size={{ xs: 12, md: 6, lg: 4 }} key={template.id}>
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-4px)',
            transition: 'transform 0.2s ease-in-out',
            boxShadow: 3
          }
        }}
      >
        {template.isPopular && (
          <Chip
            label="Popular"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          />
        )}
        
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: template.color, 
                mr: 2,
                width: 48,
                height: 48
              }}
            >
              {template.icon}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="h3" gutterBottom>
                {template.name}
              </Typography>
              <Chip
                label={template.difficulty}
                color={getDifficultyColor(template.difficulty) as any}
                size="small"
              />
            </Box>
            <IconButton
              onClick={() => handleToggleFavorite(template.id)}
              color={template.isFavorite ? 'warning' : 'default'}
            >
              {template.isFavorite ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Box>

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 2, minHeight: 60 }}
          >
            {template.description}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ScheduleIcon sx={{ fontSize: 16, mr: 1 }} />
              {template.estimatedDuration}
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MoneyIcon sx={{ fontSize: 16, mr: 1 }} />
              ${template.estimatedBudget.min.toLocaleString()} - ${template.estimatedBudget.max.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <TeamIcon sx={{ fontSize: 16, mr: 1 }} />
              {template.teamSize.min}-{template.teamSize.max} team members
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Key Skills:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {template.requiredSkills.slice(0, 3).map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  variant="outlined"
                />
              ))}
              {template.requiredSkills.length > 3 && (
                <Chip
                  label={`+${template.requiredSkills.length - 3} more`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Used {template.usageCount} times
            {template.lastUsed && ` • Last used ${new Date(template.lastUsed).toLocaleDateString()}`}
          </Typography>
        </CardContent>

        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            size="small"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewDialog(template)}
          >
            Preview
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={() => handleUseTemplate(template)}
          >
            Use Template
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Project Templates
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose from our collection of pre-designed project templates to kickstart your development process.
        </Typography>
      </Box>

      {/* Category Filter */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={selectedCategory} 
          onChange={(e, value) => setSelectedCategory(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TEMPLATE_CATEGORIES.map((category) => (
            <Tab 
              key={category}
              label={category} 
              value={category}
            />
          ))}
        </Tabs>
      </Box>

      {/* Templates Grid */}
      <Grid container spacing={3}>
        {filteredTemplates.map(renderTemplateCard)}
      </Grid>

      {filteredTemplates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No templates found in this category
          </Typography>
        </Box>
      )}

      {/* Template Preview Dialog */}
      <Dialog 
        open={Boolean(previewDialog)} 
        onClose={() => setPreviewDialog(null)}
        maxWidth="md"
        fullWidth
      >
        {previewDialog && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: previewDialog.color, mr: 2 }}>
                  {previewDialog.icon}
                </Avatar>
                <Box>
                  <Typography variant="h6">{previewDialog.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {previewDialog.category} • {previewDialog.difficulty}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {previewDialog.description}
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Project Overview
                    </Typography>
                    <Typography><strong>Duration:</strong> {previewDialog.estimatedDuration}</Typography>
                    <Typography><strong>Budget:</strong> ${previewDialog.estimatedBudget.min.toLocaleString()} - ${previewDialog.estimatedBudget.max.toLocaleString()}</Typography>
                    <Typography><strong>Team Size:</strong> {previewDialog.teamSize.min}-{previewDialog.teamSize.max} members</Typography>
                    <Typography><strong>Difficulty:</strong> {previewDialog.difficulty}</Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Required Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {previewDialog.requiredSkills.map((skill) => (
                        <Chip key={skill} label={skill} size="small" />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Features & Deliverables</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                      <List dense>
                        {previewDialog.features.map((feature, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>Deliverables:</Typography>
                      <List dense>
                        {previewDialog.deliverables.map((deliverable, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <TaskIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={deliverable} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Project Milestones</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {previewDialog.milestones.map((milestone, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primary={milestone.title}
                          secondary={`${milestone.description} (${milestone.estimatedDays} days)`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewDialog(null)}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleUseTemplate(previewDialog);
                  setPreviewDialog(null);
                }}
                startIcon={<CopyIcon />}
              >
                Use This Template
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProjectTemplatesPage;